import React, {
	memo,
	forwardRef,
	useState,
	useEffect,
	useCallback,
	useMemo,
	useRef,
} from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import "@disco/disco_core/dist/index.css";
import parse from "html-react-parser";

import useTrack from "../../hooks/useTrack";
import anim from "../../utils/anim";
import getImgUrl from "../../utils/getImgUrl";
import windowRedirect from "../../utils/windowRedirect";

import TextFit from "react-textfit";
import { BsChevronDown, BsChevronUp, BsArrowRightShort } from "react-icons/bs";
import { FaMinus as MinusIcon, FaPlus as PlusIcon } from "react-icons/fa";
import { MdCheck } from "react-icons/md";
import { HiOutlineChevronDown as DownIcon } from "react-icons/hi";

import renderExtensionReward from "../../utils/extension-previews/renderExtensionReward";
import renderExtensionSurvey from "../../utils/extension-previews/renderExtensionSurvey";
import renderExtensionCustomPost from "../../utils/extension-previews/renderExtensionCustomPost";

import "./core-ui.css";
import { EXTENSION_PREVIEW_SKIP_BTN_CLASS } from "../../conf";

import useDimensions from "../../hooks/useDimensions";
import genClassName from "../../utils/genClassName";
import isFunction from "../../utils/isFunction";
import useTimer from "../../hooks/useTimer";
import useModal from "../../hooks/useModal";
import useCounter from "../../hooks/useCounter";
import isArray from "../../utils/isArray";
import accessibilityProvider from "../../utils/accessibilityProvider";
import Lottie from "react-lottie";
import CONFETTI_ANIMATION_DATA from "../../lotties/confetti-extended-slowed.json";

export const Loader = memo(
	({
		small = false,
		center = false,
		light = false,
		dark = false,
		className = "",
		pageCenter = false,
		...rest
	}) => {
		const { innerHeight } = useDimensions();
		const height = pageCenter ? innerHeight : false;
		return (
			<div
				className={`loader ${center ? "loader-center" : ""} ${
					small ? "loader-small" : ""
				} ${light ? "loader-light" : ""} ${
					dark ? "loader-dark" : ""
				} ${className}`}
				style={{
					bottom: height ? "initial" : "0",
					top: height ? height / 2 - 10 + "px" : "0",
				}}
				{...rest}
			>
				<div className="loader-circle" />
			</div>
		);
	}
);

export const Badge = memo(({ children, className = "", ...props }) => (
	<motion.div className={`badge ${className}`} {...props}>
		{children}
	</motion.div>
));

export const Tag = memo(({ className, theme = false, ...rest }) => {
	return (
		<div
			className={`tag ${theme ? "tag-theme" : ""} ${className}`}
			{...rest}
		/>
	);
});

export const StatusLabel = memo(
	({
		children,
		type = "error",
		className = "",
		shadow = false,
		icon = false,
		...props
	}) => {
		return (
			<motion.div
				className={genClassName({
					base: "status-label",
					conditionals: {
						"status-label-success": type === "success",
						"status-label-theme": type === "theme",
						"status-label-light": type === "light",
						"status-label-shadow": shadow,
						"status-label-icon": icon,
					},
					additional: className,
				})}
				{...accessibilityProvider({
					role: "alert",
				})}
				{...props}
			>
				{children}
			</motion.div>
		);
	}
);

export const Timer = memo(
	({
		duration,
		start = true,
		onFinish,
		repeat = false,
		className = "",
		animated = false,
		pause = false,
		valueOnly = false,
		reset = false,
		preFilled = false,
		...props
	}) => {
		const [time, setTime] = useState(0);
		const timeAnimator = useAnimation();
		const regulator = useRef(null);
		const pauseTime = useRef(-1);

		const initTimer = useCallback(() => {
			setTime(duration);
			if (animated) {
				timeAnimator.set({ width: "0%" });
				timeAnimator.start({
					width: "100%",
					transition: {
						type: "linear",
						duration,
					},
				});
			}
			regulator.current = setInterval(() => {
				setTime((time) => time - 1);
			}, 1000);
		}, [duration, timeAnimator, animated]);

		const cleanUp = useCallback(() => {
			clearInterval(regulator.current);
		}, []);

		const handleRepeat = useCallback(() => {
			if (onFinish && typeof onFinish === "function") {
				onFinish();
			}
			initTimer();
		}, [onFinish, initTimer]);

		// Non-conventional solution for pausing timer
		useEffect(() => {
			if (pause && pauseTime.current === -1) {
				pauseTime.current = Math.max(time, 0);
				setTime(10000000);
			} else if (!pause && pauseTime.current !== -1) {
				setTime(pauseTime.current);
				pauseTime.current = -1;
			}
		}, [pause, time]);

		useEffect(() => {
			return () => {
				cleanUp();
			};
		}, [cleanUp]);

		useEffect(() => {
			if (!start) {
				return;
			}
			setTime(duration);
			initTimer();
		}, [setTime, duration, initTimer, start]);

		useEffect(() => {
			if (time < 0) {
				clearInterval(regulator.current);
				!repeat ? onFinish() : handleRepeat();
				return;
			}
		}, [time, onFinish, initTimer, repeat, handleRepeat]);

		useEffect(() => {
			if (!reset || !animated) {
				return;
			}
			timeAnimator.set({ width: "0%" });
		}, [reset, animated, timeAnimator]);

		if (valueOnly) {
			return pause && pauseTime.current !== -1
				? pauseTime.current
				: Math.max(time, 0);
		}
		return (
			<>
				{!animated && (
					<div className={`timer num-timer ${className}`} {...props}>
						Offer expires in{" "}
						<span>
							{pause && pauseTime.current !== -1
								? pauseTime.current
								: Math.max(time, 0)}
						</span>{" "}
						seconds
					</div>
				)}
				{animated && (
					<div
						className={`timer animated-timer ${className}`}
						{...props}
					>
						<div className="animated-timer-tube">
							<motion.div
								className="animated-timer-liq"
								initial={{ width: preFilled ? "100%" : "0%" }}
								transition={{ ease: "linear", duration }}
								animate={timeAnimator}
							/>
						</div>
					</div>
				)}
			</>
		);
	}
);

export const PriceTag = memo(
	({
		children,
		oldPrice,
		className = "",
		absolutePos = false,
		curved = false,
		padded = false,
	}) => {
		const price = children;
		return (
			<section
				className={genClassName({
					base: "price-tag",
					conditionals: {
						"price-tag-absolute-pos": absolutePos,
						"price-tag-curved": curved,
						"price-tag-padded": padded,
					},
					additional: className,
				})}
			>
				${price ? price : oldPrice ? oldPrice : price}
				{oldPrice && price ? <span>${oldPrice}</span> : null}
			</section>
		);
	}
);

export const Input = memo(
	({
		icon,
		small = false,
		hasButton = true,
		buttonProps,
		customVariant = false,
		customButton = null,
		transparent = false,
		noShadow = false,
		className = "",
		classNameInput = "",
		onClick,
		children,
		value,
		error = false,
		setError,
		autoDismissError = true,
		...props
	}) => {
		const [inputVal, setInputVal] = useState(value || "");
		const [showButton, setButton] = useState(inputVal.length > 0);

		useTimer({
			numTicks: 2,
			playing: autoDismissError && error,
			setPlaying:
				autoDismissError && isFunction(setError) ? setError : null,
			resetOnComplete: true,
		});

		const handleChange = (e) => {
			const val = e.target.value;
			val.length > 0 ? setButton(true) : setButton(false);
			setInputVal(e.target.value);
		};

		const handleButtonClick = useCallback(() => {
			if (typeof onClick !== "function") {
				return;
			}
			onClick(inputVal);
		}, [onClick, inputVal]);

		const inputButtonProps = buttonProps || {
			children: <BsArrowRightShort />,
			type: "button",
			themeGradientLight: true,
			circular: true,
			small: true,
			icon: true,
		};

		const inputButton =
			!hasButton || !showButton
				? null
				: customButton || (
						<Button
							onClick={handleButtonClick}
							className="input-button"
							variants={anim.button}
							initial="initial"
							animate="animate"
							exit="exit"
							{...inputButtonProps}
						>
							{inputButtonProps?.children || (
								<BsArrowRightShort />
							)}
						</Button>
				  );

		return (
			<motion.div
				className={genClassName({
					base: "input-container",
					conditionals: {
						"input-container-small": small,
						"input-container-transparent": transparent,
						"input-no-shadow": noShadow,
					},
					additional: className,
				})}
				variants={customVariant || anim.inputContainer}
				initial="initial"
				animate="animate"
				exit="exit"
			>
				{icon && <span className="input-icon">{icon}</span>}
				<input
					className={`input ${classNameInput}`}
					value={inputVal}
					onChange={handleChange}
					{...props}
				/>
				{children}
				<AnimatePresence>{inputButton}</AnimatePresence>
				<AnimatePresence>
					{error && (
						<StatusLabel
							variants={anim.scaleFade}
							initial="initial"
							animate="animate"
						>
							{error}
						</StatusLabel>
					)}
				</AnimatePresence>
			</motion.div>
		);
	}
);

export const BlurOverlay = memo(
	({ children, className = "", disableAnimation = false, ...props }) => {
		return (
			<motion.section
				className={`blur-overlay-container ${className}`}
				variants={disableAnimation ? anim.dialogQuick : anim.dialog}
				initial={disableAnimation ? "" : "initial"}
				animate="animate"
				exit="exit"
				{...props}
			>
				{children}
			</motion.section>
		);
	}
);

export const Label = memo(
	({ className = "", children, required, ...props }) => (
		<label className={`label ${className}`} {...props}>
			{required && <span className="label-required">* </span>}
			{children}
		</label>
	)
);
export const Select = memo(
	forwardRef(
		(
			{
				data,
				options,
				value,
				onChange,
				name,
				onFocus,
				onOpen,
				onClose,
				emptyMessage,
				required,
				placeholder = "",
				className = "",
				style,
				searchOnly = false,
				onMouseEnter,
				multiple,
				loading,
				nested = false,
				expandable = false,
				independantExpandable = false,
				mapExpandableAreaProps,
				renderExpandableArea,
				empty = true,
				onHover,
				label,
				variantSelector = false,
				variantStock,
			},
			ref
		) => {
			const [open, setOpen] = useState(false);
			const [highlightedKey, setHighlightedKey] = useState(-1);
			const selectRef = useRef(null);
			const searchRef = useRef(null);
			const [search, setSearch] = useState("");
			const [error, setError] = useState("");

			const uid = useRef("select-" + new Date().getTime()).current;

			const handleChange = (value, keyIndex) => {
				if (variantSelector && variantStock && variantStock[keyIndex]) {
					return;
				}
				onChange({ target: { name, value } });
				setOpen(multiple);
				if (!multiple) {
					setError("");
				}
			};

			const flatOptions = useMemo(
				() =>
					nested
						? Object.entries(options).reduce(
								(flat, [, sub]) => ({ ...flat, ...sub }),
								{}
						  )
						: options,
				[options, nested]
			);

			const canClose = useCallback(() => {
				if (!nested || !required) {
					return empty || (value && value.length > 0);
				}

				const done = Object.entries(options).reduce(
					(doneMap, [key]) => ({ ...doneMap, [key]: false }),
					{}
				);

				value.forEach((id) => {
					const parKey = Object.entries(options).find(
						([, val]) =>
							Object.entries(val).findIndex(
								([innerId]) => innerId === id
							) !== -1
					);
					if (!parKey) {
						return;
					}
					done[parKey[0]] = true;
				});

				return Object.entries(done).reduce(
					(ret, [, val]) => ret && val,
					true
				);
			}, [nested, options, value, empty, required]);

			const handleToggle = (e, force) => {
				// if (e) {
				// 	e.stopPropagation();
				// }

				setOpen((open) => {
					const newOpen = force !== undefined ? force : !open;
					if (newOpen) {
						if (onOpen && typeof onOpen === "function") {
							onOpen({ target: { name } });
						}
						if (searchRef.current) {
							searchRef.current.focus();
						}
					} else if (!newOpen) {
						if (!canClose()) {
							setError(
								nested
									? "Please select at least one subcategory from each category"
									: "Please select at least one"
							);
							return true;
						}
						if (onClose && typeof onClose === "function") {
							onClose({ target: { name, away: false } });
						}
						setSearch("");
						setError("");
					}
					return newOpen;
				});
			};

			useEffect(() => {
				const handleClickAway = (e) => {
					if (e.target.closest("#" + uid)) {
						return e.preventDefault();
					}
					if (open) {
						return setOpen(false);
					}
				};
				document.addEventListener("click", handleClickAway);
				return () =>
					document.removeEventListener("click", handleClickAway);
			}, [open, uid, onClose, name]);

			const handleOptionMouseEnter = useCallback(
				(key) => {
					if (onHover && typeof onHover === "function") {
						onHover(data.current, key);
					}
					setHighlightedKey(key);
				},
				[data, onHover]
			);
			const renderOptions = (options, search, extraClass = "") => {
				return Object.entries(options)
					.filter(([, text]) =>
						search.trim()
							? text
									.trim()
									.toLowerCase()
									.indexOf(search.toLowerCase()) !== -1
							: !searchOnly
					)
					.map(([key, text], optionID) => (
						<section
							className={`select-option ${extraClass} ${
								variantSelector &&
								variantStock &&
								variantStock[optionID]
									? "select-option-tall"
									: ""
							}`}
							onClick={() => handleChange(key, optionID)}
							onMouseEnter={() => handleOptionMouseEnter(key)}
							// variants={anim.selectOption}
							key={key}
						>
							{multiple && value.indexOf(key) !== -1 && (
								<span>
									<MdCheck />
								</span>
							)}
							{text}
							{variantSelector &&
								variantStock &&
								variantStock[optionID] && (
									<span className="select-option-after">
										<i>Out of Stock</i>
									</span>
								)}
						</section>
					));
			};

			// const [catOpen, setCatOpen] = useState(
			// 	Array(Object.entries(options).length)
			// );

			// const renderNestedOptions = (options, search) => {
			// 	return Object.entries(options).map(([key, subOptions], i) => (
			// 		<Collapsable
			// 			// variants={anim.selectOption}
			// 			className="select-option-group"
			// 			key={key}
			// 			open={catOpen[i]}
			// 			heading={
			// 				<>
			// 					{Object.entries(subOptions).reduce(
			// 						(found, [opt]) => {
			// 							return (
			// 								found || value.indexOf(opt) !== -1
			// 							);
			// 						},
			// 						false
			// 					) && (
			// 						<span className="select-option-group-icon">
			// 							<MdCheck />
			// 						</span>
			// 					)}
			// 					{key}
			// 				</>
			// 			}
			// 			setOpen={(fn) => {
			// 				setCatOpen((catOpen) => [
			// 					...catOpen.slice(0, i),
			// 					fn(catOpen[i], ...catOpen.slice(fn(i + 1))),
			// 				]);
			// 			}}
			// 		>
			// 			{renderOptions(subOptions, search)}
			// 		</Collapsable>
			// 	));
			// };

			const handleFocus = () => {
				if (typeof onFocus === "function") {
					onFocus({ target: { name } });
				}
			};

			const handleMouseEnter = () => {
				if (typeof onMouseEnter === "function") {
					onMouseEnter({ target: { name } });
				}
			};

			const [showValue, hasValue] = useMemo(() => {
				let selected = 0;
				const res =
					multiple && value.length > 0
						? value
								.filter((key) => !!flatOptions[key])
								.map((key) => {
									selected++;
									return (
										<Badge key={flatOptions[key]} listItem>
											{flatOptions[key]}
										</Badge>
									);
								})
						: flatOptions[value];
				return [res, multiple ? selected : flatOptions[value]];
			}, [value, multiple, flatOptions]);

			return (
				<>
					{label && <Label className="select-label">{label}</Label>}
					{error && open && (
						<StatusLabel className="select-error">
							{error}{" "}
						</StatusLabel>
					)}
					<section
						ref={selectRef}
						id={uid}
						className={`select ${
							options[value] || value.length ? "" : "select-empty"
						} ${
							expandable ? "select-expandable" : ""
						} ${className}`}
						style={{
							zIndex: open ? 101 : 100,
							width: expandable && open ? "200%" : "",
							...style,
						}}
						tabIndex={0}
						onFocus={handleFocus}
						onMouseEnter={handleMouseEnter}
					>
						<motion.span
							className="select-icon"
							animate={{ rotate: open ? 180 : 0 }}
							onClick={handleToggle}
						>
							<DownIcon />
						</motion.span>

						<main
							className="select-selected"
							ref={ref}
							onClick={handleToggle}
						>
							{loading && <Loader small />}
							{hasValue ? showValue : placeholder}
						</main>
						<AnimatePresence>
							{open && (
								<motion.section
									className={`select-options ${
										expandable
											? "select-options-expandable"
											: ""
									}`}
									variants={anim.select}
									initial="initial"
									animate="animate"
									exit="exit"
								>
									<div>
										{loading && <Loader />}
										{!loading &&
											(Object.entries(options).length >
											0 ? (
												<>
													{/* {searchable && (
														<Input
															ref={searchRef}
															value={search}
															placeholder="Start typing to search"
															onChange={(e) =>
																setSearch(
																	e.target
																		.value
																)
															}
														/>
													)}
													{nested
														? renderNestedOptions(
																options,
																search
														  )
														: renderOptions(
																options,
																search
														  )} */}
													{renderOptions(
														options,
														search
													)}
												</>
											) : (
												<section className="select-no-options">
													{emptyMessage}
												</section>
											))}
									</div>
									{expandable && (
										<section
											className={`select-options-expandable-area ${
												independantExpandable
													? "select-options-expandable-independant-area"
													: ""
											}`}
										>
											{!independantExpandable && (
												<>
													<h2 className="select-options-expandable-area-title">
														Preview Title
													</h2>
													<div className="select-options-expandable-area-body" />
												</>
											)}
											{independantExpandable &&
												renderExpandableArea(
													mapExpandableAreaProps(
														data.current,
														highlightedKey
													)
												)}
										</section>
									)}
								</motion.section>
							)}
						</AnimatePresence>
					</section>
				</>
			);
		}
	)
);

export const ImageSelector = memo(
	({
		imgArray,
		className = "",
		selected = 0,
		onChange,
		direct = false,
		activeRef,
		...props
	}) => {
		const handleClick = useCallback(
			(value) => {
				if (!onChange || typeof onChange !== "function") {
					return;
				}
				onChange(imgArray[value], value);
			},
			[onChange, imgArray]
		);

		if (!Array.isArray(imgArray) || imgArray.length < 1) {
			return null;
		}

		return (
			<section className={`img-selector ${className}`} {...props}>
				{imgArray.map((imgItem, index) => (
					<img
						key={`${imgItem}_${index}`}
						onClick={() => handleClick(index)}
						className={`${
							index === selected ? "img-selector-selected" : ""
						}`}
						ref={index === selected ? activeRef : null}
						src={direct ? imgItem : getImgUrl(imgItem, 50, 50)}
						alt=""
					/>
				))}
			</section>
		);
	}
);

export const ImageCarousel = memo(
	({ direct = false, imgArray, imgUrl = "", className = "", ...props }) => {
		const [activeImgIdx, setActiveImgIdx] = useState(0);

		const hasMultipleImages = useMemo(
			() => Array.isArray(imgArray) && imgArray.length > 1,
			[imgArray]
		);

		const handleChange = useCallback((_, idx) => setActiveImgIdx(idx), []);

		return (
			<section className={`img-carousel ${className || ""}`}>
				<img
					src={
						hasMultipleImages
							? direct
								? imgArray[activeImgIdx]
								: getImgUrl(imgArray[activeImgIdx])
							: imgUrl
					}
					alt=""
					className="img-carousel-primary-img"
				></img>
				{hasMultipleImages && (
					<ImageSelector
						imgArray={imgArray}
						direct={direct}
						select={activeImgIdx}
						onChange={handleChange}
						{...props}
					/>
				)}
			</section>
		);
	}
);

export const RoundedCard = memo(
	forwardRef(({ className = "", children, ...props }, ref) => {
		return (
			<motion.section
				className={`rounded-card ${className}`}
				ref={ref}
				{...props}
			>
				{children}
			</motion.section>
		);
	})
);

export const ExpandableContainer = memo(
	({
		className = "",
		children,
		expandOpenFooter,
		triggerExpand = false,
		disableExpandBtn = false,
		onExpand,
		showExpandThreshold,
		...props
	}) => {
		const [expanded, setExpanded] = useState(false);

		const handleClick = useCallback(() => {
			setExpanded(true);
			if (onExpand && typeof onExpand === "function") {
				onExpand();
			}
		}, [onExpand]);

		useEffect(() => {
			if (!triggerExpand) {
				return;
			}
			handleClick();
		}, [triggerExpand, handleClick]);

		useEffect(() => {
			if (!showExpandThreshold || typeof children !== "string") {
				return;
			}
			if (children.length <= showExpandThreshold) {
				setExpanded(true);
			}
		}, [showExpandThreshold, children]);
		return (
			<section
				className={`expandable-container ${
					expanded ? "expandable-container-expanded" : ""
				} ${className}`}
				{...props}
			>
				{children}
				{!expanded && (
					<div
						className="expandable-container-footer"
						onClick={disableExpandBtn ? () => {} : handleClick}
					>
						{expandOpenFooter || "Learn More"}
					</div>
				)}
			</section>
		);
	}
);

export const BadgeButton = memo(
	({ className = "", badgeItem = false, children, ...props }) => (
		<div
			className={`badge-button ${className} ${
				badgeItem ? "badge-button-item" : ""
			}`}
			{...props}
		>
			{children}
		</div>
	)
);

export const InlineQuantityPicker = memo(
	({
		className = "",
		quantity = 1,
		setQuantity,
		minQuantity = 1,
		maxQuantity = 5,
		...props
	}) => {
		const handleChange = (add = false) => {
			if (!setQuantity || typeof setQuantity !== "function") {
				return;
			}
			if (
				(add && quantity === maxQuantity) ||
				(!add && quantity === minQuantity)
			) {
				return;
			}
			setQuantity((quantity) => (add ? quantity + 1 : quantity - 1));
		};

		return (
			<section
				className={`inline-quantity-picker ${className}`}
				{...props}
			>
				<div
					className={`inline-quantity-icon ${
						quantity === minQuantity
							? "inline-quantity-icon-disabled"
							: ""
					}`}
					onClick={() => handleChange(false)}
				>
					<MinusIcon />
				</div>
				<div className="inline-quantity-text">{quantity}</div>
				<div
					className={`inline-quantity-icon ${
						quantity === maxQuantity
							? "inline-quantity-icon-disabled"
							: ""
					}`}
					onClick={() => handleChange(true)}
				>
					<PlusIcon />
				</div>
			</section>
		);
	}
);

export const PostPurchaseExtensionPreview = memo(
	({ surfaceData, handleClose, className = "", type = "", ...props }) => {
		useEffect(() => {
			document
				.querySelectorAll(`.${EXTENSION_PREVIEW_SKIP_BTN_CLASS}`)
				.forEach((btn) => btn.addEventListener("click", handleClose));

			return () => {
				document
					.querySelectorAll(`.${EXTENSION_PREVIEW_SKIP_BTN_CLASS}`)
					.forEach((btn) =>
						btn.removeEventListener("click", handleClose)
					);
			};
		}, [handleClose]);

		const args = { ...surfaceData };

		return (
			<section
				className={`post-purchase-extension-preview ${className}`}
				{...props}
			>
				{parse(
					type === "reward"
						? renderExtensionReward(args)
						: type === "survey"
						? renderExtensionSurvey(args)
						: type === "custom_post"
						? renderExtensionCustomPost(args)
						: ""
				)}
			</section>
		);
	}
);

// UNDER REVIEW

const CardOption = memo(
	({ option, optionID, onClick, selected, handleInput }) => {
		const handleOptionClick = useCallback(() => {
			if (typeof onClick !== "function") {
				return;
			}
			onClick(optionID);
		}, [onClick, optionID]);
		const { icon_url, text, icon, text_input, follow_text } = useMemo(
			() => option,
			[option]
		);
		return (
			<motion.div
				className={`card-option ${
					text_input && selected ? "card-option-other" : ""
				}`}
				onClick={handleOptionClick}
				variants={anim.cardOption}
				{...accessibilityProvider({
					tabIndex: 0,
					role: "listitem",
				})}
			>
				{!selected &&
					(icon || icon_url) &&
					(icon ? (
						<span
							className="card-option-img card-option-icon-img"
							{...accessibilityProvider({
								role: "button",
								name: `Icon for ${text}`,
								disableTabIndex: true,
							})}
						>
							{icon_url}
						</span>
					) : (
						<img
							src={icon_url}
							className="card-option-img"
							alt="Option Icon"
							role="button"
						/>
					))}
				{!selected && (
					<div
						className="card-option-text"
						{...accessibilityProvider({
							role: "button",
							name: text,
						})}
					>
						{text}
					</div>
				)}
				<AnimatePresence>
					{text_input && selected && (
						<Input
							small
							placeholder={
								follow_text
									? follow_text
									: `${text}: Please enter your response`
							}
							onClick={handleInput}
							transparent
							noShadow
							{...accessibilityProvider({
								tabIndex: 0,
							})}
						/>
					)}
				</AnimatePresence>
			</motion.div>
		);
	}
);

export const Card = memo(
	({
		heading,
		completion,
		options,
		handleParent,
		isDefault,
		modalAnswer,
		handleIntermediateResponse,
		...props
	}) => {
		const [open, setOpen] = useState(true);
		const [answer, setAnswer] = useState(-1);
		const [otherAnswerVal, setOtherAnswerVal] = useState("");

		const handleAnswer = useCallback(
			(key) => {
				if (answer !== key) {
					setAnswer(key);
					const textOption = options[key].text_input;
					setOpen(textOption);
					if (!textOption) {
						return handleParent(key);
					}
					if (
						textOption &&
						typeof handleIntermediateResponse === "function"
					) {
						handleIntermediateResponse(key);
					}
				}
			},
			[handleParent, options, answer, handleIntermediateResponse]
		);

		const handleOtherResponse = useCallback(
			(val) => {
				setOpen(false);
				setOtherAnswerVal(val);
				handleParent(answer, val);
			},
			[answer, handleParent]
		);

		useEffect(() => {
			if (modalAnswer > -1) {
				setAnswer(modalAnswer);
				setOpen(false);
				setOtherAnswerVal("");
				handleParent(modalAnswer, "", false);
			}
		}, [modalAnswer, options, handleParent]);

		return (
			<motion.section
				className={genClassName({
					base: "card-root",
					conditionals: {
						"card-root-complete": !open,
					},
				})}
				// className={`card-root ${!open ? "card-root-complete" : ""} ${
				// false ? "card-root-full-width" : ""
				// fullWidthCard ? "card-root-full-width" : ""
				// }`}
				{...props}
			>
				<div
					className={`card-head ${!open ? "card-head-complete" : ""}`}
					{...accessibilityProvider({
						role: "group",
						disableTabIndex: true,
					})}
				>
					<div
						className="card-head-content"
						{...accessibilityProvider({
							role: "banner",
							name: heading,
						})}
					>
						{open
							? heading
							: isDefault
							? `${completion}${
									completion !== ""
										? ` ${options[answer].extra_text || ""}`
										: ""
							  }`
							: ""}
						{answer >= 0 && (
							<span
								className="card-head-answer"
								style={
									!isDefault
										? {
												fontSize: "1.2rem",
										  }
										: {}
								}
							>
								{options[answer].icon ? (
									<span
										className="card-head-image"
										{...accessibilityProvider({
											role: "img",
											disableTabIndex: true,
										})}
									>
										{options[answer].icon_url}
									</span>
								) : (
									<img
										src={options[answer].icon_url}
										className="card-head-image"
										alt="Icon"
									/>
								)}
								{options[answer].text_input
									? otherAnswerVal.length === 0
										? options[answer].text
										: otherAnswerVal
									: options[answer].text}
							</span>
						)}
					</div>
					<div className="card-head-arrow" role="presentation">
						{open ? <BsChevronDown /> : <BsChevronUp />}
					</div>
				</div>
				<AnimatePresence>
					{open && (
						<motion.div
							initial="initial"
							animate="animate"
							exit="exit"
							variants={anim.cardBody}
							className="card-body"
							role="list"
						>
							{options.map((option, key) => {
								return (
									<CardOption
										key={option.text}
										option={option}
										optionID={key}
										onClick={handleAnswer}
										selected={key === answer}
										handleInput={handleOtherResponse}
									/>
								);
							})}
						</motion.div>
					)}
				</AnimatePresence>
			</motion.section>
		);
	}
);
// --------------------------------------------------------

export const ThemeButton = memo(
	({
		className = "",
		iconOnly = false,
		iconStyle = {},
		icon = null,
		event = false,
		children,
		link = "",
		bold = false,
		...props
	}) => {
		const track = useTrack();

		const handleEvent = () => {
			if (event) {
				track(event, { link });
			}
			if (link) {
				windowRedirect({
					url: link,
				});
			}
		};

		return (
			<button
				className={genClassName({
					base: "theme-button",
					conditionals: {
						"theme-button-icon-only": iconOnly,
						"theme-button-bold": bold,
					},
					additional: className,
				})}
				onClick={handleEvent}
				{...props}
			>
				{!bold && icon && (
					<span style={iconStyle} className="theme-button-icon">
						{icon}
					</span>
				)}
				{children}
				{bold && icon && (
					<div style={iconStyle} className="theme-button-icon">
						{icon}
					</div>
				)}
			</button>
		);
	}
);

export const DummyImage = memo(({ ...props }) => {
	return (
		<img
			alt={props?.alt || ""}
			style={{ display: "none", ...props?.style }}
			{...props}
		/>
	);
});

export const HorizontalSeparator = memo(({ className, ...rest }) => {
	return (
		<section
			className={genClassName({
				base: "horizontal-separator",
				additional: className,
			})}
			{...rest}
		></section>
	);
});

export const StoryLine = memo(
	({
		idx = 0,
		totalNum = 0,
		duration = 2,
		canPlay = true,
		onDone,
		className = "",
		white = false,
	}) => {
		if (totalNum < 1) {
			return null;
		}

		return (
			<section
				key={idx}
				className={genClassName({
					base: "story-line",
					conditionals: { "story-line-white": white },
					additional: className,
				})}
			>
				{canPlay
					? [...Array(totalNum)].map((_, currIdx) => (
							<Timer
								pause={true}
								key={currIdx}
								duration={duration}
								start={currIdx === idx}
								onFinish={onDone}
								animated
								preFilled={idx > currIdx}
							/>
					  ))
					: null}
			</section>
		);
	}
);

export const ShadowText = memo(
	({ children, className = "", ellipsis = false, ...rest }) => {
		return (
			<div
				className={genClassName({
					base: "shadow-text",
					conditionals: {
						"shadow-text-ellipsis": ellipsis,
					},
					additional: className,
				})}
				{...rest}
			>
				{children}
			</div>
		);
	}
);

export const Button = memo(
	forwardRef(
		(
			{
				children,
				type = "button",
				className = "",

				small = false,
				large = false,
				xLarge = false,
				curved = false,
				circular = false,
				shadow = false,
				shadowLight = false,
				icon = false,

				themeGradient = false,
				themeGradientLight = false,
				themeText = false,

				dark = false,
				gray = false,
				grayGradient = false,
				discoGradient = false,
				simple = false,

				light = false,
				lightGradient = false,

				grayBorder = false,
				grayDarkBorder = false,
				themeBorder = false,
				fullWidth = false,

				showLoading = false,
				loadingDuration = 2,
				whileLoading,
				onLoadComplete = false,

				onLoadClick,
				onClick,

				...props
			},
			ref
		) => {
			const [triggerLoad, setTriggerLoad] = useState(false);
			const eventRef = useRef(null);

			const onTimerComplete = useCallback(() => {
				setTriggerLoad(false);
				const e = eventRef.current;
				eventRef.current = null;
				if (!isFunction(onLoadComplete)) return;
				onLoadComplete(e);
			}, [onLoadComplete]);

			useTimer({
				numTicks: loadingDuration,
				playing: triggerLoad,
				resetOnComplete: true,
				onComplete: onTimerComplete,
			});

			const handleClick = (e) => {
				if (triggerLoad) return;
				if (showLoading) {
					if (isFunction(onLoadClick)) {
						onLoadClick(e);
					}
					eventRef.current = e;
					return setTriggerLoad(true);
				}
				if (!isFunction(onClick)) return;
				onClick(e);
			};

			return (
				<motion.button
					type={type}
					ref={ref}
					className={genClassName({
						base: "btn",
						conditionals: {
							"btn-curved": curved,
							"btn-circular": circular,
							"btn-light": light,
							"btn-dark": dark,
							"btn-gray": gray,
							"btn-small": small,
							"btn-large": large,
							"btn-x-large": xLarge,
							"btn-disco-gradient": discoGradient,
							"btn-gray-gradient": grayGradient,
							"btn-theme-gradient": themeGradient,
							"btn-theme-gradient-light": themeGradientLight,
							"btn-with-icon": icon,
							"btn-shadow": shadow,
							"btn-shadow-light": shadowLight,
							"btn-theme-border": themeBorder,
							"btn-theme-text": themeText,
							"btn-light-gradient": lightGradient,
							"btn-gray-border": grayBorder,
							"btn-gray-dark-border": grayDarkBorder,
							"btn-full-width": fullWidth,
							"btn-loading": triggerLoad,
							"btn-simple": simple,
						},
						additional: className,
					})}
					onClick={handleClick}
					style={{
						"--loading-duration": `${loadingDuration}s`,
						...(props?.style || {}),
					}}
					{...props}
				>
					{triggerLoad && whileLoading ? whileLoading : children}
				</motion.button>
			);
		}
	)
);

export const Tooltip = memo(
	({
		className = "",
		heading = "",
		children,
		themeBorder = false,
		shadow = false,
		blur = false,
		open = false,
		...rest
	}) => {
		const {
			open: isMouseOver,
			handleOpen: handleMouseEnter,
			handleClose: handleMouseLeave,
		} = useModal(false);

		const isTooltipOpen = isMouseOver || open;

		return (
			<AnimatePresence>
				{isTooltipOpen && (
					<motion.section
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						className={genClassName({
							base: "tooltip",
							additional: className,
							conditionals: {
								"tooltip-theme-border": themeBorder,
								"tooltip-shadow": shadow,
								"tooltip-blur": blur,
							},
						})}
						variants={anim.tooltip}
						initial="initial"
						animate="animate"
						exit="exit"
						{...rest}
					>
						{heading && (
							<h2 className="tooltip-heading">{heading}</h2>
						)}
						{children}
					</motion.section>
				)}
			</AnimatePresence>
		);
	}
);

export const ProgressView = memo(({ progress = 0, className = "" }) => {
	return (
		<section
			className={genClassName({
				base: "progress-view",
				additional: className,
			})}
		>
			<section
				className="progress-view-bar"
				style={{ width: progress * 100 + "%" }}
			/>
		</section>
	);
});

export const LoadingScreenBar = memo(
	({
		className = "",
		steps,
		initialLoadProgress = 0.1,
		stepDuration = 1,
		delayOnComplete = 0,
		onComplete,
	}) => {
		const numSteps = useMemo(
			() => (isArray(steps) ? steps.length : 0),
			[steps]
		);
		const { counter: activeStep, increment: nextStep } = useCounter({
			max: numSteps,
		});
		const [timerPlaying, setTimerPlaying] = useState(true);

		const areStepsComplete = useMemo(
			() => activeStep === numSteps,
			[activeStep, numSteps]
		);

		const { heading, duration, text } = useMemo(
			() => steps?.[areStepsComplete ? activeStep - 1 : activeStep] || {},
			[steps, activeStep, areStepsComplete]
		);

		const numTicks = useMemo(
			() =>
				areStepsComplete ? delayOnComplete : duration || stepDuration,
			[duration, stepDuration, areStepsComplete, delayOnComplete]
		);

		const triggerComplete = useCallback(() => {
			if (!timerPlaying) return;
			setTimerPlaying(false);
			if (!isFunction(onComplete)) return;
			onComplete();
		}, [onComplete, timerPlaying]);

		const handleComplete = useCallback(() => {
			if (activeStep === numSteps) return triggerComplete();
			return nextStep();
		}, [activeStep, numSteps, nextStep, triggerComplete]);

		const barProgress = useMemo(() => {
			if (activeStep === 0) {
				return initialLoadProgress;
			}
			const unit = (1 - initialLoadProgress) / numSteps;
			return initialLoadProgress + activeStep * unit;
		}, [activeStep, initialLoadProgress, numSteps]);

		useTimer({
			numTicks,
			resetOnComplete: true,
			playing: timerPlaying,
			onComplete: handleComplete,
		});

		return (
			<motion.section
				className={genClassName({
					base: "loading-screen-bar",
					additional: className,
				})}
			>
				<TextFit
					className="loading-screen-bar-heading flex-center"
					mode="multi"
				>
					{heading}
				</TextFit>
				<ProgressView progress={barProgress} />
				<section className="loading-screen-bar-text">{text}</section>
			</motion.section>
		);
	}
);

const SuccessConfetti = ({ offsetLeft = "0%", onComplete }) => {
	const handleComplete = () => {
		if (!isFunction(onComplete)) return;
		onComplete();
	};

	// 	const colors = [
	// 		"#D1DEF1",
	// 		"#DAE9FF",
	// 		"#ECF3FF",
	// 		"#F2ECFF",
	// 		"#E9DEFF",
	// 	];

	return (
		<Lottie
			options={{
				autoplay: true,
				loop: false,
				animationData: CONFETTI_ANIMATION_DATA,
				rendererSettings: {
					preserveAspectRatio: "xMidYMid slice",
				},
			}}
			style={{
				position: "absolute",
				left: offsetLeft,
				bottom: 0,
			}}
			width={"50%"}
			eventListeners={[
				{
					eventName: "complete",
					callback: handleComplete,
				},
			]}
			height={"100%"}
		/>
	);
};

export const SuccessConfettiManager = memo(({ canPlay }) => {
	const [run, setRun] = useState(false);

	useEffect(() => {
		if (!canPlay) return;
		setRun(true);
	}, [canPlay]);

	const handleComplete = () => setRun(false);

	if (!run) return null;
	return (
		<section className="success-confetti-manager">
			<SuccessConfetti offsetLeft="0%" onComplete={handleComplete} />
			<SuccessConfetti offsetLeft="50%" />
		</section>
	);
});
